<template>
  <Teleport to="#modal">
    <div class="fixed inset-0 bg-black z-max">
      <video :class="!hasRecording && 'hidden'" ref="recordedVideo" class="h-full w-full" playsinline :controls="!autoplayEnabled"/>
      <video :class="hasRecording && 'hidden'" ref="gumVideo" class="h-full w-full" playsinline autoplay muted/>
      <div :class="!hasRecording && 'hidden'"
           class="absolute right-0 bottom-0 left-0 flex items-center justify-between bg-gradient-to-b from-transparent to-black p-12 text-white">
        <button @click="reRecord" type="button" class="text-lg">Redo</button>
        <button ref="playButton" type="button" @click="togglePlay()"
                class="cursor-pointer transition-all hover:brightness-90">
          <PauseIcon v-if="isPlaying" class="h-6 w-6"/>
          <PlayIcon v-else class="h-6 w-6"/>
        </button>
        <button @click="save(true)" type="button" class="text-lg">Done</button>
      </div>
      <div :class="hasRecording && 'hidden'"
           class="absolute right-0 bottom-0 left-0 flex items-center justify-between bg-gradient-to-b from-transparent to-black p-12 text-white">
        <button type="button" @click="openFileInput">
          <PhotoIcon class="h-6 w-6"/>
          <input ref="fileInput" type="file" accept="video/mp4,video/webm" @change="setFile" class="sr-only"/>
        </button>
        <button type="button" ref="recordButton" class="cursor-pointer self-center transition-all hover:brightness-90">
          <CustomIconStartRecording v-if="!isRecording"/>
          <CustomIconStopRecording v-else/>
        </button>
        <button type="button" @click="toggleFacingMode()"
                :class="!toggleFacingModeSupported && 'cursor-not-allowed opacity-50'">
          <ArrowsRightLeftIcon class="h-6 w-6"/>
        </button>
      </div>
      <div v-if="prompt"
           class="absolute bottom-40 left-1/2 w-fit max-w-lg -translate-x-1/2 rounded-full bg-black bg-opacity-50 px-3 text-center text-white py-1.5">
        {{ prompt }}
      </div>
      <div v-if="!hasPermissions"
           class="absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center">
        <div>
          <div class="flex items-center justify-center gap-1 text-white">
            <VideoCameraIcon class="h-4 w-4"/>
            Enable Video
          </div>
          <div class="mt-4 flex items-center justify-center gap-1 text-white">
            <MicrophoneIcon class="h-4 w-4"/>
            Enable Microphone
          </div>
        </div>
      </div>
      <button type="button" class="absolute top-0 right-0 cursor-pointer text-white" @click="closeRecorder()">
        <XMarkIcon class="h-12 w-12 p-2"/>
      </button>
      <div v-if="!hasRecording" class="absolute top-0 flex items-center gap-2 p-4 text-red-600"
           :class="!elapsedSeconds && 'invisible'">
        <div class="h-2 w-2 animate-pulse rounded-full bg-red-600 ring-2 ring-red-600 ring-offset-1"/>
        <p class="sr-only">Recording</p>
      </div>

      <div v-if="!hasRecording"
           class="absolute top-0 left-1/2 flex -translate-x-1/2 items-center gap-2 pt-4 text-white">
        <span class="opacity-50">{{ timeRemainingReadable }}</span>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import CustomIconStartRecording from "./CustomIconStartRecording.vue";
import CustomIconStopRecording from "./CustomIconStopRecording.vue";
import {ArrowsRightLeftIcon, PauseIcon, PlayIcon, XMarkIcon} from "@heroicons/vue/24/solid/index.js";
import {MicrophoneIcon, PhotoIcon, VideoCameraIcon} from "@heroicons/vue/24/outline/index.js";
import {useVideoRecorder} from "../composables/VideoRecorderComposable.js";
import {onBeforeUnmount, onMounted} from "vue";

const props = defineProps({
  path: String,
  prompt: String,
  file: Object,
  maxSeconds: {
    type: Number,
    default: 60,
  },
});


const emits = defineEmits([
  "close",
  "save",
  "update:path",
  "update:file",
  "update:path",
  "update:isOpen",
]);

const {
  elapsedSeconds,
  recordButton,
  playButton,
  hasRecording,
  isRecording,
  isPlaying,
  gumVideo,
  fileInput,
  recordedVideo,
  hasPermissions,
  autoplayEnabled,
  timeRemainingReadable,
  toggleFacingModeSupported,
  debouncedStartStream,
  handlePlay,
  handlePause,
  togglePlay,
  toggleIsRecording,
  reRecord,
  init,
  save,
  closeRecorder,
  toggleFacingMode,
  openFileInput,
  setFile,
  stopAllTracks,
} = useVideoRecorder();

onMounted(async () => {
  await init(props, emits);
  if (recordedVideo.value) recordedVideo.value.addEventListener("playing", handlePlay);
  if (recordedVideo.value) recordedVideo.value.addEventListener("pause", handlePause);
  window.addEventListener("resize", debouncedStartStream);
});

onBeforeUnmount(() => {
  stopAllTracks();
  if(recordButton.value) recordButton.value.removeEventListener("click", toggleIsRecording);
  window.removeEventListener("resize", debouncedStartStream);
  if (recordedVideo.value) recordedVideo.value.removeEventListener("playing", handlePlay);
  if (recordedVideo.value) recordedVideo.value.removeEventListener("pause", handlePause);
});
</script>
